// src/utils/googleCalendarSync.js
// Single source of truth for Google Calendar ↔ Mylo event mapping

export const APP_TIMEZONE = 'America/Chicago';

const WORK_KEYWORDS = ['shift','er ','northshore','endeavor','nocturnist','overnight','7p','7a','19:00','on call'];

export function detectWorkEvent(gEvent) {
  const text = `${gEvent.summary||''} ${gEvent.description||''}`.toLowerCase();
  return WORK_KEYWORDS.some(k => text.includes(k));
}

export function detectWorkEventCustom(gEvent, customKeywords = []) {
  const allKW = [...WORK_KEYWORDS, ...customKeywords];
  const text = `${gEvent.summary||''} ${gEvent.description||''}`.toLowerCase();
  return allKW.some(k => text.includes(k));
}

export function mapGoogleEventToMylo(gEvent) {
  const isWork = detectWorkEvent(gEvent);
  const allDay = !!gEvent.start?.date;
  return {
    id: `gcal_${gEvent.id}`,
    googleId: gEvent.id,
    title: gEvent.summary || 'untitled',
    date: gEvent.start?.date || gEvent.start?.dateTime?.split('T')[0],
    time: gEvent.start?.dateTime
      ? new Date(gEvent.start.dateTime).toLocaleTimeString('en-US', {
          hour: 'numeric', minute: '2-digit', hour12: true,
        })
      : null,
    endDate: gEvent.end?.date || gEvent.end?.dateTime?.split('T')[0],
    endTime: gEvent.end?.dateTime
      ? new Date(gEvent.end.dateTime).toLocaleTimeString('en-US', {
          hour: 'numeric', minute: '2-digit', hour12: true,
        })
      : null,
    allDay,
    location: gEvent.location || null,
    description: gEvent.description || null,
    source: 'google',
    calendarId: gEvent.calendarId || 'primary',
    calendarName: gEvent.calendarName || null,
    color: gEvent.colorId || null,
    recurring: !!gEvent.recurringEventId,
    recurringId: gEvent.recurringEventId || null,
    status: gEvent.status || 'confirmed',
    updatedAt: gEvent.updated || null,
    type: isWork ? 'work' : 'event',
    category: isWork ? 'work' : 'personal',
  };
}

export function mapMyloEventToGoogle(mEvent) {
  function convertTo24Hour(timeStr) {
    if (!timeStr) return '09:00';
    const [time, modifier] = timeStr.split(' ');
    let [hours, minutes] = time.split(':');
    if (modifier === 'PM' && hours !== '12') hours = parseInt(hours) + 12;
    if (modifier === 'AM' && hours === '12') hours = '00';
    return `${String(hours).padStart(2,'0')}:${minutes}`;
  }
  return {
    summary: mEvent.title,
    start: mEvent.allDay
      ? { date: mEvent.date }
      : { dateTime: `${mEvent.date}T${convertTo24Hour(mEvent.time)}:00`, timeZone: APP_TIMEZONE },
    end: mEvent.allDay
      ? { date: mEvent.endDate || mEvent.date }
      : { dateTime: `${mEvent.date}T${convertTo24Hour(mEvent.endTime || mEvent.time)}:00`, timeZone: APP_TIMEZONE },
    description: mEvent.description || '',
    location: mEvent.location || '',
  };
}

export function mergeEvents(existingEvents, googleEvents) {
  const googleMapped = googleEvents.map(mapGoogleEventToMylo);
  const nonGoogleEvents = (existingEvents || []).filter(e => e.source !== 'google');
  const deduped = googleMapped.filter(ge =>
    !nonGoogleEvents.some(e => e.googleId === ge.googleId)
  );
  return [...nonGoogleEvents, ...deduped];
}

export function removeWorkoutEvent(workoutId, existingEvents) {
  return (existingEvents || []).filter(e => e.id !== `train_${workoutId}`);
}

export function createWorkoutEvent(workout, existingEvents = []) {
  const RUN_TYPES      = ['Long Run','Easy Run','Tempo Run','Interval'];
  const STRENGTH_TYPES = ['Strength','Gym'];
  function resolveDot(type) {
    if (RUN_TYPES.includes(type))      return 'training-run';
    if (STRENGTH_TYPES.includes(type)) return 'training-strength';
    return 'training-other';
  }
  const duplicate = (existingEvents || []).some(e =>
    e.id === `train_${workout.id}` ||
    (e.date === workout.date && e.source === 'train' && e.workoutType === workout.type)
  );
  if (duplicate) return null;
  const distStr = workout.dist > 0 ? ` · ${workout.dist}mi` : '';
  const noteStr = workout.note ? ` · ${workout.note}` : '';
  return {
    id: `train_${workout.id}`,
    title: `${workout.type}${distStr}${noteStr}`,
    date: workout.date,
    time: workout.time || '07:00',
    type: 'health',
    category: 'training',
    source: 'train',
    workoutType: workout.type,
    dist: workout.dist || 0,
    dot: resolveDot(workout.type),
  };
}

export function syncWorkoutToCalendar(workout, setSt) {
  setSt(p => {
    const newEvent = createWorkoutEvent(workout, p.events);
    if (!newEvent) return p;
    return { ...p, events: [...(p.events || []), newEvent] };
  });
}
